import React from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PROMO_POPUP_DEFAULT } from '../config/promoPopup';

/**
 * PromoPopupModal — presentational only. Given a merged `promo` object
 * (defaults + admin overrides) and an `onCtaClick` / `onClose` pair, it
 * renders the promo card. No fetch, no localStorage, no side effects —
 * that way the admin editor can reuse it as a live preview.
 *
 * Design mirrors the coach's mock (navy `#0B1B33`-ish background with a
 * champagne-gold accent). The layout is fully responsive: on mobile it
 * shrinks to a single column; on md+ it keeps the poster proportions.
 */

const withDefaults = (promo) => ({ ...PROMO_POPUP_DEFAULT, ...(promo || {}) });

const PromoPopupCard = ({ promo, onCtaClick, onClose, embedded = false }) => {
  const p = withDefaults(promo);

  return (
    <div
      data-testid="promo-popup-card"
      className={`relative w-full max-w-[560px] mx-auto ${embedded ? '' : 'shadow-2xl'} bg-[#0B1B33] text-white rounded-2xl overflow-hidden border border-yellow-600/40`}
      style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}
    >
      {/* Close button (top-right) */}
      {onClose && (
        <button
          type="button"
          data-testid="promo-popup-close"
          onClick={onClose}
          aria-label="Bezárás"
          className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 text-white/90 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500/60"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Inner border frame */}
      <div className="p-5 sm:p-7 border border-yellow-600/25 rounded-2xl m-1.5">
        {/* Brand + venue */}
        <div className="flex items-start justify-between gap-3 mb-6 sm:mb-8">
          <div className="min-w-0">
            <div
              data-testid="promo-popup-brand"
              className="text-[13px] sm:text-[15px] font-extrabold tracking-[0.18em] leading-tight"
            >
              {p.brand}
            </div>
            <div className="text-[9px] sm:text-[10px] tracking-[0.24em] text-white/60 mt-1 font-semibold">
              {p.brand_sub}
            </div>
          </div>
          <div className="text-[9px] sm:text-[10px] font-bold text-yellow-400 tracking-[0.22em] pt-1 shrink-0 text-right">
            {p.venue}
          </div>
        </div>

        {/* Eyebrow */}
        <div
          data-testid="promo-popup-eyebrow"
          className="text-[11px] sm:text-[12px] font-bold text-yellow-400 tracking-[0.32em] mb-3"
        >
          {p.eyebrow}
        </div>

        {/* Headline */}
        <div className="mb-4">
          <div
            data-testid="promo-popup-headline"
            className="text-[38px] sm:text-[54px] font-black leading-[0.95] tracking-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {p.headline_line1}
            <br />
            {p.headline_line2}
          </div>
        </div>

        {/* Headline badge (gold rectangle) */}
        {p.headline_badge && (
          <div className="inline-block mb-5">
            <div
              data-testid="promo-popup-headline-badge"
              className="bg-yellow-500 text-[#0B1B33] font-black text-lg sm:text-xl tracking-[0.18em] px-5 py-2 rounded-sm"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {p.headline_badge}
            </div>
          </div>
        )}

        {/* Feature bullet */}
        <div className="mb-5">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-6 h-6 text-yellow-400 shrink-0" strokeWidth={2} />
            <div className="text-lg sm:text-xl font-bold">
              {p.bullet_lead}{' '}
              <span className="text-yellow-400">{p.bullet_accent}</span>
            </div>
          </div>
          {p.bullet_sub && (
            <div className="text-[12px] sm:text-[13px] text-white/70 mt-1.5 ml-8">
              {p.bullet_sub}
            </div>
          )}
        </div>

        {/* Discount block */}
        {p.discount_amount && (
          <div className="mb-6 rounded-xl bg-[#1a2a44] border-l-4 border-yellow-500 px-4 py-3.5">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div
                data-testid="promo-popup-discount"
                className="text-2xl sm:text-3xl font-black text-yellow-400 leading-none"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {p.discount_amount}
              </div>
              <div className="text-sm sm:text-base font-bold text-white/90">
                {p.discount_sub}
              </div>
            </div>
            {p.discount_fine && (
              <div className="text-[11px] sm:text-[12px] text-white/60 mt-1.5">
                {p.discount_fine}
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          data-testid="promo-popup-cta"
          onClick={onCtaClick}
          className="group w-full bg-yellow-500 hover:bg-yellow-400 text-[#0B1B33] font-black text-[13px] sm:text-[15px] tracking-[0.14em] rounded-lg px-5 py-4 flex items-center justify-center gap-3 transition-all duration-200 hover:shadow-2xl hover:shadow-yellow-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1B33] cursor-pointer"
        >
          <span>{p.cta_label}</span>
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" strokeWidth={3} />
        </button>

        {/* Footer */}
        <div className="flex items-center justify-between mt-5 text-[11px] sm:text-[12px] text-white/70 font-medium">
          <div data-testid="promo-popup-website">{p.website}</div>
          <div data-testid="promo-popup-phone">{p.phone}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Full-screen overlay wrapper. Use in production; the admin preview uses
 * <PromoPopupCard /> directly.
 */
const PromoPopupModal = ({ open, promo, onCtaClick, onClose }) => {
  React.useEffect(() => {
    if (!open) return undefined;
    // Close on ESC
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    // Prevent background scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="promo-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-popup-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        // Backdrop click closes; card clicks don't bubble
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div className="max-h-[92vh] overflow-y-auto w-full flex items-center justify-center">
        <PromoPopupCard
          promo={promo}
          onCtaClick={onCtaClick}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

export { PromoPopupCard };
export default PromoPopupModal;
